import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input, Button } from '@/components/ui';

import { useSystemSettings } from '../hooks/useSystemSettings';

const generalSchema = z.object({
  siteName: z.string().min(2, "Tên hệ thống quá ngắn"),
  siteDescription: z.string().optional(),
  supportEmail: z.string().email("Email không hợp lệ"),
  hotline: z.string().min(10, "Hotline không hợp lệ"),
  footerText: z.string().optional(),
});

export type GeneralFormValues = z.infer<typeof generalSchema>;

interface GeneralSectionProps {
  onChange?: (isDirty: boolean) => void;
  onSaveSuccess?: () => void;
}

export function GeneralSection({ onChange, onSaveSuccess }: GeneralSectionProps) {
  const { settings, isLoading } = useSystemSettings();
  
  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<GeneralFormValues>({
    resolver: zodResolver(generalSchema),
    defaultValues: settings || {}
  });

  // Sync data when loaded
  React.useEffect(() => {
    if (settings) {
      reset(settings);
    }
  }, [settings, reset]);

  React.useEffect(() => {
    if (onChange) {
      onChange(isDirty);
    }
  }, [isDirty, onChange]);

  const { updateSettings, isUpdating } = useSystemSettings();

  const onSubmit = async (data: GeneralFormValues) => {
    try {
      await updateSettings(data);
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      // Error is handled by mutation onError
    }
  };

  return (
    <form id="general-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700">Tên hệ thống <span className="text-red-500">*</span></label>
          <Input 
            {...register('siteName')} 
            placeholder="Ví dụ: Landscape SaaS" 
            className={errors.siteName ? 'border-red-500' : ''}
          />
          {errors.siteName && <p className="text-[10px] text-red-500 font-bold">{errors.siteName.message}</p>}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700">Hotline hỗ trợ <span className="text-red-500">*</span></label>
          <Input 
            {...register('hotline')} 
            placeholder="0xxx xxx xxx" 
            className={errors.hotline ? 'border-red-500' : ''}
          />
          {errors.hotline && <p className="text-[10px] text-red-500 font-bold">{errors.hotline.message}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-bold text-slate-700">Mô tả hệ thống</label>
        <textarea 
          {...register('siteDescription')}
          rows={3}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
          placeholder="Nhập mô tả ngắn về hệ thống..."
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-bold text-slate-700">Email hỗ trợ <span className="text-red-500">*</span></label>
        <Input 
          {...register('supportEmail')} 
          type="email"
          placeholder="support@example.com" 
          className={errors.supportEmail ? 'border-red-500' : ''}
        />
        {errors.supportEmail && <p className="text-[10px] text-red-500 font-bold">{errors.supportEmail.message}</p>}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-bold text-slate-700">Thông tin bản quyền (Footer)</label>
        <Input 
          {...register('footerText')} 
          placeholder="© 2026 Company Name" 
        />
      </div>
    </form>
  );
}
